import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Trash2, Edit, X, GripVertical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface TarotCard {
  id?: string;
  card_name: string;
  card_number: number;
  suit: string;
  upright_meaning: string;
  reversed_meaning: string;
  keywords: string[];
  story: string;
  imagery_explanation?: string;
  image_url?: string;
  is_published: boolean;
  sort_order: number;
}

function SortableCard({ card, onEdit, onDelete, uploadingImage, onImageUpload }: {
  card: TarotCard;
  onEdit: (card: TarotCard) => void;
  onDelete: (id: string) => void;
  uploadingImage: boolean;
  onImageUpload: (cardId: string, file: File) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: card.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card ref={setNodeRef} style={style}>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div 
            className="cursor-grab active:cursor-grabbing flex-shrink-0 pt-2"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-6 w-6 text-muted-foreground" />
          </div>
          
          <div className="flex-shrink-0">
            {card.image_url ? (
              <img
                src={card.image_url}
                alt={card.card_name}
                className="w-32 h-48 object-cover rounded"
              />
            ) : (
              <div className="w-32 h-48 bg-muted rounded flex items-center justify-center">
                <Upload className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            <div className="mt-2">
              <Label
                htmlFor={`upload-${card.id}`}
                className="cursor-pointer inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Upload className="h-4 w-4" />
                {card.image_url ? 'Replace' : 'Upload'} Image
              </Label>
              <Input
                id={`upload-${card.id}`}
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploadingImage}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && card.id) onImageUpload(card.id, file);
                }}
              />
            </div>
          </div>

          <div className="flex-1 space-y-2">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg">{card.card_name}</h3>
                <p className="text-sm text-muted-foreground">{card.suit} • #{card.card_number}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => onEdit(card)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => card.id && onDelete(card.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex gap-1 flex-wrap">
              {card.keywords.map((kw, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">{kw}</Badge>
              ))}
            </div>

            <p className="text-sm line-clamp-2">{card.story}</p>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>Sort: {card.sort_order}</span>
              <Badge variant={card.is_published ? 'default' : 'outline'}>
                {card.is_published ? 'Published' : 'Draft'}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TarotCardsManager() {
  const [cards, setCards] = useState<TarotCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCard, setEditingCard] = useState<TarotCard | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('tarot_cards')
      .select('*')
      .order('sort_order');

    if (error) {
      toast({ title: 'Error loading cards', description: error.message, variant: 'destructive' });
    } else {
      setCards(data || []);
    }
    setLoading(false);
  };

  const handleImageUpload = async (cardId: string, file: File) => {
    setUploadingImage(true);
    try {
      // Upload to tarot-images bucket
      const fileName = `${cardId}-${Date.now()}.${file.name.split('.').pop()}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('tarot-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('tarot-images')
        .getPublicUrl(fileName);

      // Update card with image URL
      const { error: updateError } = await supabase
        .from('tarot_cards')
        .update({ image_url: publicUrl })
        .eq('id', cardId);

      if (updateError) throw updateError;

      toast({ title: 'Image uploaded successfully!' });
      loadCards();
    } catch (error: any) {
      toast({ title: 'Error uploading image', description: error.message, variant: 'destructive' });
    } finally {
      setUploadingImage(false);
    }
  };

  const saveCard = async (card: TarotCard) => {
    if (card.id) {
      const { error } = await supabase
        .from('tarot_cards')
        .update(card)
        .eq('id', card.id);

      if (error) {
        toast({ title: 'Error updating card', description: error.message, variant: 'destructive' });
        return;
      }
    } else {
      const { error } = await supabase
        .from('tarot_cards')
        .insert(card);

      if (error) {
        toast({ title: 'Error creating card', description: error.message, variant: 'destructive' });
        return;
      }
    }

    toast({ title: card.id ? 'Card updated' : 'Card created' });
    setEditingCard(null);
    setIsCreating(false);
    loadCards();
  };

  const deleteCard = async (id: string) => {
    if (!confirm('Are you sure you want to delete this card?')) return;

    const { error } = await supabase
      .from('tarot_cards')
      .delete()
      .eq('id', id);

    if (error) {
      toast({ title: 'Error deleting card', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Card deleted' });
      loadCards();
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = cards.findIndex((card) => card.id === active.id);
      const newIndex = cards.findIndex((card) => card.id === over.id);

      const newCards = arrayMove(cards, oldIndex, newIndex);
      
      // Update sort_order for all affected cards
      const updates = newCards.map((card, index) => ({
        id: card.id,
        sort_order: index
      }));

      setCards(newCards);

      // Update in database
      try {
        for (const update of updates) {
          await supabase
            .from('tarot_cards')
            .update({ sort_order: update.sort_order })
            .eq('id', update.id);
        }
        toast({ title: 'Card order updated' });
      } catch (error: any) {
        toast({ title: 'Error updating order', description: error.message, variant: 'destructive' });
        loadCards(); // Reload to get correct order
      }
    }
  };

  const CardForm = ({ card, onSave, onCancel }: { card: TarotCard; onSave: (card: TarotCard) => void; onCancel: () => void }) => {
    const [formData, setFormData] = useState<TarotCard>(card);
    const [keywordInput, setKeywordInput] = useState('');

    const addKeyword = () => {
      if (keywordInput.trim()) {
        setFormData({ ...formData, keywords: [...formData.keywords, keywordInput.trim()] });
        setKeywordInput('');
      }
    };

    const removeKeyword = (index: number) => {
      setFormData({ ...formData, keywords: formData.keywords.filter((_, i) => i !== index) });
    };

    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{card.id ? 'Edit Card' : 'New Card'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Card Name</Label>
              <Input
                value={formData.card_name}
                onChange={(e) => setFormData({ ...formData, card_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Card Number</Label>
              <Input
                type="number"
                value={formData.card_number}
                onChange={(e) => setFormData({ ...formData, card_number: parseInt(e.target.value) })}
              />
            </div>
          </div>

          <div>
            <Label>Suit</Label>
            <Select value={formData.suit} onValueChange={(value) => setFormData({ ...formData, suit: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="major">Major Arcana</SelectItem>
                <SelectItem value="cups">Cups</SelectItem>
                <SelectItem value="pentacles">Pentacles</SelectItem>
                <SelectItem value="swords">Swords</SelectItem>
                <SelectItem value="wands">Wands</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Keywords</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                placeholder="Add keyword"
              />
              <Button type="button" onClick={addKeyword}>Add</Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {formData.keywords.map((kw, idx) => (
                <Badge key={idx} variant="secondary" className="gap-1">
                  {kw}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => removeKeyword(idx)} />
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <Label>Story</Label>
            <Textarea
              value={formData.story}
              onChange={(e) => setFormData({ ...formData, story: e.target.value })}
              rows={4}
            />
          </div>

          <div>
            <Label>Imagery Explanation</Label>
            <Textarea
              value={formData.imagery_explanation || ''}
              onChange={(e) => setFormData({ ...formData, imagery_explanation: e.target.value })}
              rows={4}
              placeholder="Describe what's shown in the Rider-Waite image and what each symbol represents..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Explain the visual elements in detail so users can understand and remember the card through its imagery
            </p>
          </div>

          <div>
            <Label>Upright Meaning</Label>
            <Textarea
              value={formData.upright_meaning}
              onChange={(e) => setFormData({ ...formData, upright_meaning: e.target.value })}
              rows={3}
            />
          </div>

          <div>
            <Label>Reversed Meaning</Label>
            <Textarea
              value={formData.reversed_meaning}
              onChange={(e) => setFormData({ ...formData, reversed_meaning: e.target.value })}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label>Published</Label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>Save</Button>
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tarot Cards Manager</CardTitle>
          <CardDescription>
            Upload Rider-Waite card images and manage tarot card content
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => { setIsCreating(true); setEditingCard(null); }}>
            Add New Card
          </Button>
        </CardContent>
      </Card>

      {(isCreating || editingCard) && (
        <CardForm
          card={editingCard || {
            card_name: '',
            card_number: 0,
            suit: 'major',
            upright_meaning: '',
            reversed_meaning: '',
            keywords: [],
            story: '',
            is_published: true,
            sort_order: 0
          }}
          onSave={saveCard}
          onCancel={() => { setEditingCard(null); setIsCreating(false); }}
        />
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={cards.map(c => c.id!)}
          strategy={verticalListSortingStrategy}
        >
          <div className="grid gap-4">
            {cards.map((card) => (
              <SortableCard
                key={card.id}
                card={card}
                onEdit={setEditingCard}
                onDelete={deleteCard}
                uploadingImage={uploadingImage}
                onImageUpload={handleImageUpload}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}